/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import { InView } from 'react-intersection-observer'
import { debounce, agnostify, isEmpty } from '@sainsburys-tech/boltui-utils'
import { Slider } from '@sainsburys-tech/bolt'
import { ZoomIcon } from '@sainsburys-tech/bolt-icons'
import { withCookies } from 'react-cookie'
import config from './config/media-gallery.config'
import Analytics from './helpers/media-gallery.tracking'
import ThumbnailComponent from './components/Thumbnails/Thumbnails'
import VideoPlayerComponent from './components/VideoPlayer/VideoPlayer'
import MediaGalleryGrid from '../MadeToOrderComponents/MediaGalleryGrid/MediaGalleryGrid'
import Arkit from '../ArKit/Arkit'
import getImageIndexWithFlag from '../../helpers/getImageIndexWithFlag'
import { GalleryModal } from './components/GalleryModal/GalleryModal'
import ShopSimilarButton from './components/ShopSimilarButton/ShopSimilarButton'
import VideoWithTextIcon from './icons/VideoWithTextIcon'
import ThreeSixtyWithTextIcon from './icons/ThreeSixtyWithTextIcon'
import ViewInThreeDIcon from './icons/ViewInThreeDIcon'

import * as Styled from './MediaGallery.styles'

const ThreeSixtyComponent = loadable(() =>
  import(/* webpackChunkName: "three-sixty-component" */ './components/ThreeSixty/ThreeSixty')
)
const PinchZoom = loadable(() => import(/* webpackChunkName: "pinch-zoom" */ './components/PinchZoom/PinchZoom'))

const mediaMissingImage = '//media.4rgos.it/i/Argos/prod-img-placeholder'
const breakpoints = {
  xs: 'only screen and (min-width: 320px)',
  sm: 'only screen and (min-width: 576px)',
  md: 'only screen and (min-width: 768px)',
  lg: 'only screen and (min-width: 992px)'
}

const deviceType = {
  touch: '(pointer:coarse)'
}

const getImageDimensions = (
  src,
  isPortrait,
  isReviews,
  imgIsLandscape = false,
  showMediaGalleryGrid = false,
  isModalImage = false
) => {
  const { sizes, madeToOrderSizes } = config.image
  const imgQlt = isModalImage ? 100 : 70
  const avifQlt = isModalImage ? 100 : 45

  // If the media gallery grid is shown, then use made to order image sizes
  const imageSize = showMediaGalleryGrid ? madeToOrderSizes : sizes

  let desktopWidth = isPortrait ? imageSize.portrait.desktop.width : imageSize.default.desktop.width
  let desktopHeight = isPortrait ? imageSize.portrait.desktop.height : imageSize.default.desktop.height
  let mobileWidth = isPortrait ? imageSize.portrait.mobile.width : imageSize.default.mobile.width
  let mobileHeight = isPortrait ? imageSize.portrait.mobile.height : imageSize.default.mobile.height

  if (isReviews) {
    desktopWidth = !imgIsLandscape ? imageSize.portrait.desktop.width : imageSize.default.desktop.width
    desktopHeight = !imgIsLandscape ? imageSize.portrait.desktop.height : imageSize.default.desktop.height
    mobileWidth = !imgIsLandscape ? imageSize.portrait.mobile.width : imageSize.default.mobile.width
    mobileHeight = !imgIsLandscape ? imageSize.portrait.mobile.height : imageSize.default.mobile.height
  }

  const defaultDimensions = {
    desktop: {
      src: `${src}?w=${desktopWidth}&h=${desktopHeight}&qlt=${imgQlt}`,
      srcSet: `${src}?w=${desktopWidth * 2}&h=${desktopHeight * 2}&qlt=${imgQlt}`,
      srcSetAvif: `${src}?w=${desktopWidth * 2}&h=${desktopHeight * 2}&qlt=${avifQlt}`,
      width: desktopWidth,
      height: desktopHeight
    },
    mobile: {
      src: `${src}?w=${mobileWidth}&h=${mobileHeight}&qlt=${imgQlt}`,
      srcSet: `${src}?w=${mobileWidth * 2}&h=${mobileHeight * 2}&qlt=${imgQlt}`,
      srcSetAvif: `${src}?w=${mobileWidth * 2}&h=${mobileHeight * 2}&qlt=${avifQlt}`,
      width: mobileWidth,
      height: mobileHeight
    }
  }

  if (isModalImage) {
    return {
      desktop: {
        src: `${src}?qlt=${imgQlt}`,
        srcSet: `${src}?qlt=${imgQlt}`,
        srcSetAvif: `${src}?qlt=${avifQlt}`
      },
      mobile: defaultDimensions.mobile
    }
  }

  return {
    ...defaultDimensions
  }
}

class MediaGallery extends React.Component {
  _isMounted = false

  constructor(props) {
    super(props)

    this.sliderRef = React.createRef()
    this.mainRef = React.createRef()
    this.threeSixtyRef = React.createRef()
    this.mainImageRef = React.createRef()

    this.state = {
      trackingIndex: 0,
      currentSlide: 0,
      mainImageZoomed: false,
      orderedMedia: this.orderMedia(this.props.media, this.props.gltf),
      isMobile: false,
      disableSwipe: false,
      isTouch: false,
      componentIsFocused: false,
      indexesLoaded: [0],
      imgIsLandscape: {},
      isGalleryModalOpen: false,
      pausedIndex: 0
    }
  }

  componentDidMount() {
    this._isMounted = true
    if (this.props.isReviews && this.props.media?.images) {
      this.props.media.images.forEach(imgUrl => {
        const img = new Image()
        img.src = imgUrl
        img.onload = () => {
          this.setState({
            imgIsLandscape: { ...this.state.imgIsLandscape, [imgUrl.replace('https:', '')]: img.width >= img.height }
          })
        }
      })
    }

    this.setState({
      isMobile: this.isMobile(),
      isTouch: this.isTouch()
    })
    window.addEventListener('resize', this.handleResize)
  }

  componentDidUpdate(prevProps, prevState) {
    const { currentSlide, disableSwipe } = this.state
    const { currentSlide: prevSlide } = prevState
    const { media: prevMedia } = prevProps
    const { media: newMedia, gltf: newGltf } = this.props

    if (prevSlide !== currentSlide) {
      this.pausePlayingMedia()

      if (disableSwipe) {
        this.setState({
          disableSwipe: false
        })
      }
    }
    if (prevMedia !== newMedia) {
      this.pausePlayingMedia()

      this.setState({
        orderedMedia: this.orderMedia(newMedia, newGltf),
        trackingIndex: 0,
        currentSlide: 0,
        isMobile: this.isMobile(),
        mainImageZoomed: false
      })
    }
  }

  handleResize = debounce(
    () =>
      this.setState({
        isMobile: this.isMobile()
      }),
    50
  )

  componentWillUnmount() {
    this._isMounted = false
    window.removeEventListener('resize', this.handleResize)
  }

  isTouch = () => this._isMounted && window.matchMedia(deviceType.touch).matches

  isMobile = () => {
    return !window.matchMedia(breakpoints.md).matches
  }

  validateVideo = videoSource => {
    const videoRegex = /media\.4rgos\.it|i1\.adis\.ws/i
    return videoRegex.test(videoSource)
  }

  getVideoURI = videoSource => {
    const transcodeProtocol = 'mp4_480p'

    const matchesRegex = this.validateVideo(videoSource)

    const videoUri = matchesRegex ? `${videoSource}/${transcodeProtocol}?protocol=https` : videoSource

    return videoUri
  }

  pausePlayingMedia = () => {
    const playingVideos = Array.from(document.getElementsByClassName('MediaGallery_video-player-playing'))
    playingVideos.forEach(video => {
      video.pause()
    })
    if (this.threeSixtyRef.current) {
      this.threeSixtyRef.current.stopPlaying()
    }
  }

  // Added for energy label images so we can move it in the carosel, ticket num: PRO1-6019 and github link: https://github.com/sainsburys-tech/argos-pdp-ui-service/pull/389/files
  isEnergyLabel = imgUrl =>
    imgUrl.trim().substring(imgUrl.length - 4) === 'A001' || imgUrl.trim().substring(imgUrl.length - 4) === 'A002'

  isShield = imgUrl => imgUrl.trim().substring(imgUrl.length - 5) === 'Z001A'

  orderMedia = (media = [], gltf = {}) => {
    const orderedMedia = []
    const orderedImages = []
    const orderedVideos = []
    const orderedThreeSixtys = []
    const orderedThreeDImage = []
    const eneryLabels = []

    const { images = [], videos = [], threesixty = [] } = media

    if (this.props.showMediaGalleryGrid) {
      const lineDrawings = []
      const lifestyleImages = []
      const productImages = []
      const addditionalImages = []

      images.forEach(image => {
        if (image.includes('_E')) {
          lineDrawings.push({ type: 'image', src: agnostify(image), thumbnail: agnostify(image) })
        } else if (/Z00.C/.test(image)) {
          lifestyleImages.push({ type: 'image', src: agnostify(image), thumbnail: agnostify(image) })
        } else if (/Z00.A/.test(image)) {
          productImages.push({ type: 'image', src: agnostify(image), thumbnail: agnostify(image) })
        } else {
          addditionalImages.push({ type: 'image', src: agnostify(image), thumbnail: agnostify(image) })
        }
      })

      orderedImages.push(...lifestyleImages, ...productImages, ...addditionalImages, ...lineDrawings)
    } else if (images) {
      images
        .filter(image => {
          if (this.props.isProductCare) {
            return !this.isShield(image)
          }
          return true
        })
        .forEach(image => {
          if (this.isEnergyLabel(image)) {
            eneryLabels.push({ type: 'image', src: agnostify(image), thumbnail: agnostify(image) })
          } else {
            orderedImages.push({ type: 'image', src: agnostify(image), thumbnail: agnostify(image) })
          }
        })
    }

    const swatchImageIndices = getImageIndexWithFlag(orderedImages, 'B')
    if (swatchImageIndices.length > 0) {
      const swatchImage = orderedImages[swatchImageIndices[0]]
      orderedImages.splice(swatchImageIndices[0], 1)
      orderedImages.splice(1, 0, swatchImage)
      if (orderedImages[1]) {
        orderedImages[1].type = 'swatch'
      }
    }

    if (videos) {
      videos.forEach(video => {
        orderedVideos.push({
          type: 'video',
          src: agnostify(video),
          thumbnail: this.validateVideo(video) || !orderedImages.length ? agnostify(video) : orderedImages[0].thumbnail
        })
      })
    }

    if (threesixty && threesixty.length) {
      orderedThreeSixtys.push({
        type: '360',
        src: threesixty.map(item => agnostify(item)),
        thumbnail: agnostify(threesixty[0])
      })
    }

    if (gltf && gltf?.texturedModel) {
      orderedThreeDImage.push({
        type: '3D-AR',
        src: orderedImages[0].src,
        thumbnail: orderedImages[0]?.thumbnail
      })
    }

    // inAction && orderedInActions.push({ type: 'inAction', src: inAction.map((item) => item), thumbnail: inAction[0] })

    // Ordering the media in the correct order, as per the design documents
    if (orderedImages.length) {
      orderedMedia.push(orderedImages.shift())
    }
    if (orderedThreeDImage.length) {
      orderedMedia.push(orderedThreeDImage.shift())
    }

    if (orderedVideos.length) {
      orderedMedia.push(orderedVideos.shift())
    }
    if (orderedThreeSixtys.length) {
      orderedMedia.push(orderedThreeSixtys.shift())
    }

    // orderedInActions.length && orderedMedia.push(orderedInActions.shift())

    orderedImages.forEach(image => orderedMedia.push(image))
    orderedVideos.forEach(video => orderedMedia.push(video))
    orderedThreeSixtys.forEach(item => orderedMedia.push(item))
    // orderedInActions.forEach((item) => orderedMedia.push(item))
    if (eneryLabels.length) {
      eneryLabels.forEach(label => orderedMedia.push(label))
    }

    return orderedMedia
  }

  renderMainImages = (isInGrid = false, isInModal = false) => {
    const { orderedMedia, isMobile } = this.state
    const { isPortrait, productName, model, gltf, partNumber, showMediaGalleryGrid } = this.props

    if (isEmpty(orderedMedia)) {
      return [<Styled.MainImage src={mediaMissingImage} key='media-missing-image' />]
    }

    return orderedMedia.map((media, index) => {
      const { src, thumbnail, type } = media
      const isMediaGalleryGridShown = showMediaGalleryGrid && !isMobile // Checks if product is made to order & user is on desktop view

      const imageDimensions = getImageDimensions(
        src,
        isPortrait,
        false,
        this.state.imgIsLandscape[src],
        showMediaGalleryGrid,
        isInModal
      )

      switch (type) {
        case 'video':
          return isInGrid ? (
            <InView>
              {({ inView, ref }) => (
                <>
                  {this.renderImageTag(thumbnail, index, ref, inView, isInGrid, isInModal)}
                  <Styled.MediaIconWrapper>
                    <VideoWithTextIcon width={100} height={100} fill='#333' />
                  </Styled.MediaIconWrapper>
                </>
              )}
            </InView>
          ) : (
            <VideoPlayerComponent
              width={imageDimensions.width}
              height={imageDimensions.height}
              videoUri={this.getVideoURI(src)}
              thumbnail={this.validateVideo(thumbnail) ? thumbnail : orderedMedia[0].src}
              index={index}
              isMobile={isMobile}
              isInModal={isInModal}
            />
          )
        case '360':
          return isInGrid ? (
            <InView>
              {({ inView, ref }) => (
                <>
                  {this.renderImageTag(thumbnail, index, ref, inView, isInGrid, isInModal)}
                  <Styled.MediaIconWrapper>
                    <ThreeSixtyWithTextIcon width={100} height={100} fill='#333' />
                  </Styled.MediaIconWrapper>
                </>
              )}
            </InView>
          ) : (
            <ThreeSixtyComponent
              ref={index === this.state.currentSlide ? this.threeSixtyRef : null}
              width={imageDimensions.width}
              height={imageDimensions.height}
              images={src}
              autoPlay={index === this.state.currentSlide}
            />
          )
        case '3D-AR':
          return isInGrid ? (
            <InView>
              {({ inView, ref }) => (
                <>
                  {this.renderImageTag(thumbnail, index, ref, inView, isInGrid, isInModal)}
                  <Styled.MediaIconWrapper>
                    <ViewInThreeDIcon showLargeIcon />
                  </Styled.MediaIconWrapper>
                </>
              )}
            </InView>
          ) : (
            <Arkit
              productName={productName}
              model={model}
              gltf={gltf}
              partNumber={partNumber}
              src={src}
              imageDimensions={imageDimensions}
              showMediaGalleryGrid={isMediaGalleryGridShown || isInModal}
            />
          )
        case 'image':
        default:
          return <InView>{({ inView, ref }) => this.renderImageTag(src, index, ref, inView, false, isInModal)}</InView>
      }
    })
  }

  renderDefaultImage = () => {
    const { orderedMedia } = this.state
    const { isPortrait, isReviews, productName, showMediaGalleryGrid } = this.props
    const firstImageSrc = orderedMedia[0].src

    const imageDimensions = getImageDimensions(
      firstImageSrc,
      isPortrait,
      isReviews,
      this.state.imgIsLandscape[firstImageSrc],
      showMediaGalleryGrid
    )

    return (
      <Styled.ImageWrapper isPortrait={isPortrait} isReviews={isReviews} showMediaGalleryGrid={showMediaGalleryGrid}>
        <picture>
          <source
            media='(max-width: 767px)'
            type='image/webp'
            srcSet={`${imageDimensions.mobile.srcSet}&fmt=webp`}
            width={imageDimensions.mobile.width}
            height={imageDimensions.mobile.height}
          />
          <source
            media='(min-width: 768px)'
            type='image/webp'
            srcSet={`${imageDimensions.desktop.srcSet}&fmt=webp`}
            width={imageDimensions.desktop.width}
            height={imageDimensions.desktop.height}
          />
          <source
            media='(max-width: 767px)'
            type='image/jpeg'
            srcSet={`${imageDimensions.mobile.srcSet}&fmt=pjpeg`}
            width={imageDimensions.mobile.width}
            height={imageDimensions.mobile.height}
          />
          <source
            media='(min-width: 768px)'
            type='image/jpeg'
            srcSet={`${imageDimensions.desktop.srcSet}&fmt=pjpeg`}
            width={imageDimensions.desktop.width}
            height={imageDimensions.desktop.height}
          />
          <img
            itemProp='image'
            src={`${imageDimensions.desktop.src}`}
            width={imageDimensions.desktop.width}
            height={imageDimensions.desktop.height}
            key={firstImageSrc}
            alt={`${productName}`}
          />
        </picture>
      </Styled.ImageWrapper>
    )
  }

  renderImageTag = (src, index, ref, inView, isGridThumbnail, isInModal) => {
    const { isTouch, currentSlide, mainImageZoomed, indexesLoaded, isMobile } = this.state
    const { productName, isPortrait, isReviews, 'data-test': dataTest, showMediaGalleryGrid } = this.props
    const imageDimensions = getImageDimensions(
      src,
      isPortrait,
      isReviews,
      this.state.imgIsLandscape[src],
      showMediaGalleryGrid,
      isInModal
    )
    let imgIsPortrait = isPortrait
    imgIsPortrait = isReviews ? !this.state.imgIsLandscape[src] : imgIsPortrait
    const isMediaGalleryGridShown = showMediaGalleryGrid && !isMobile // Checks if product is made to order & user is on desktop view

    const wrappedImage = () => {
      const image = isInModal ? (
        <picture>
          <source type='image/webp' srcSet={`${imageDimensions.desktop.srcSet}&fmt=webp`} />
          <source type='image/jpeg' srcSet={`${imageDimensions.desktop.srcSet}&fmt=pjpeg`} />
          <img
            itemProp='image'
            src={imageDimensions.desktop.src}
            width={imageDimensions.desktop.width}
            height={imageDimensions.desktop.height}
            alt={`${productName} ${index}`}
            ref={index === currentSlide ? this.mainImageRef : null}
            onMouseMove={event => this.zoomMouseMovement(event, index)}
            onMouseEnter={event => this.zoomMouseMovement(event, index)}
            onMouseLeave={event => this.zoomMouseMovement(event, index)}
            onClick={() => this.handleZoom(isMediaGalleryGridShown, index)}
          />
        </picture>
      ) : (
        <picture>
          <source
            media='(max-width: 767px)'
            type='image/webp'
            srcSet={`${imageDimensions.mobile.srcSet}&fmt=webp`}
            width={imageDimensions.mobile.width}
            height={imageDimensions.mobile.height}
          />
          <source
            media='(min-width: 768px)'
            type='image/webp'
            srcSet={`${imageDimensions.desktop.srcSet}&fmt=webp`}
            width={imageDimensions.desktop.width}
            height={imageDimensions.desktop.height}
          />
          <source
            media='(max-width: 767px)'
            type='image/jpeg'
            srcSet={`${imageDimensions.mobile.srcSet}&fmt=pjpeg`}
            width={imageDimensions.mobile.width}
            height={imageDimensions.mobile.height}
          />
          <source
            media='(min-width: 768px)'
            type='image/jpeg'
            srcSet={`${imageDimensions.desktop.srcSet}&fmt=pjpeg`}
            width={imageDimensions.desktop.width}
            height={imageDimensions.desktop.height}
          />
          <img
            itemProp='image'
            ref={index === currentSlide && !showMediaGalleryGrid ? this.mainImageRef : null}
            src={imageDimensions.desktop.src}
            width={imageDimensions.desktop.width}
            height={imageDimensions.desktop.height}
            key={src}
            alt={`${productName} ${index}`}
            loading='lazy'
            onMouseMove={event => !isMediaGalleryGridShown && this.zoomMouseMovement(event, index)}
            onMouseEnter={event => !isMediaGalleryGridShown && this.zoomMouseMovement(event, index)}
            onMouseLeave={event => !isMediaGalleryGridShown && this.zoomMouseMovement(event, index)}
            onClick={() => this.handleZoom(showMediaGalleryGrid, index)}
          />
        </picture>
      )
      if ((isTouch && !showMediaGalleryGrid) || (isTouch && isInModal)) {
        return (
          <PinchZoom
            key={src}
            image={image}
            callbackFn={callback => this.state.trackingIndex === index && this.handlePinchAndZoom(callback)}
          />
        )
      }
      return image
    }

    if (!inView && !this.hasLazyContentLoaded(index) && !isInModal)
      return <Styled.ImageWrapper isPortrait={imgIsPortrait} showMediaGalleryGrid={showMediaGalleryGrid} ref={ref} />

    if (inView && !this.hasLazyContentLoaded(index) && !isInModal) {
      const updatedLazyIndexes = [...indexesLoaded, index]
      this.setState({ indexesLoaded: updatedLazyIndexes })
    }

    return (
      <Styled.ImageWrapper
        isPortrait={imgIsPortrait}
        isReviews={isReviews}
        mainImageZoomed={mainImageZoomed}
        data-test={`${dataTest}_activeSlide-${currentSlide}`}
        ref={ref}
        isTranslucent={isGridThumbnail}
        showMediaGalleryGrid={showMediaGalleryGrid}
        isInModal={isInModal}
      >
        {wrappedImage()}
      </Styled.ImageWrapper>
    )
  }

  hasLazyContentLoaded = index => {
    const { indexesLoaded } = this.state
    return indexesLoaded.includes(index)
  }

  handlePinchAndZoom = callback => {
    const { isZoomedIn } = callback
    if (this.state.mainImageZoomed !== isZoomedIn) this.setState({ mainImageZoomed: isZoomedIn })
  }

  handleZoom = (isMediaGalleryGridShown, index) => {
    const { mainImageZoomed, isMobile, currentSlide, orderedMedia, isGalleryModalOpen, isTouch } = this.state
    const { scale } = config.zoom

    if (isMediaGalleryGridShown && !isGalleryModalOpen) {
      this.updateTrackingIndex(index)
      this.setState({ isGalleryModalOpen: true, pausedIndex: index + 1 })
      return
    }

    if (isMobile || isTouch) return

    const activeMainImage = this.mainImageRef.current
    if (!mainImageZoomed) {
      activeMainImage.style.transform = `scale(${scale})`
      this.setState(
        prevState => ({
          mainImageZoomed: !prevState.mainImageZoomed,
          disableSwipe: !prevState.disableSwipe
        }),
        () =>
          Analytics.fire(
            currentSlide,
            'mainImageZoom',
            Analytics.componentIdFromMediaItem(orderedMedia[currentSlide]),
            window
          )
      )
    } else {
      activeMainImage.style.transform = 'scale(1)'
      this.setState(prevState => ({
        mainImageZoomed: !prevState.mainImageZoomed,
        disableSwipe: !prevState.disableSwipe
      }))
    }
  }

  zoomMouseMovement = event => {
    const { mainImageZoomed } = this.state
    const { scale } = config.zoom
    if (mainImageZoomed) {
      const activeMainImage = this.mainImageRef.current
      const leftPosition =
        (event.clientX - activeMainImage.getBoundingClientRect().left) / (activeMainImage.width * scale)
      const topPosition =
        (event.clientY - activeMainImage.getBoundingClientRect().top) / (activeMainImage.height * scale)
      activeMainImage.style.transformOrigin = `${leftPosition * 100}% ${topPosition * 100}%`
    }
  }

  updateTrackingIndex = newTrackingIndex => {
    const prevMainImageZoomed = this.state.mainImageZoomed
    this.setState({
      trackingIndex: newTrackingIndex,
      currentSlide: newTrackingIndex,
      mainImageZoomed: false
    })

    const activeMainImage = this.mainImageRef.current

    if (prevMainImageZoomed === true && activeMainImage) {
      // reset magnification
      activeMainImage.style.transform = 'scale(1)'
    }
  }

  onSlideChangedCallback = (newIndex, direction, event) => {
    const { orderedMedia, currentSlide: oldIndex } = this.state
    const mediaIndex = newIndex - 1
    this.updateTrackingIndex(mediaIndex)
    if (event && !event.isInternalUpdate) {
      Analytics.fire(
        oldIndex,
        `click on ${direction} chevron`,
        Analytics.componentIdFromMediaSource(orderedMedia[oldIndex].thumbnail),
        window
      )
    }
  }

  setComponentFocus = isFocused => {
    this.setState({
      componentIsFocused: isFocused
    })
  }

  render() {
    const {
      isPortrait,
      'data-test': dataTest,
      model,
      partNumber,
      showShopSimilarButton,
      gltf,
      productName,
      showMediaGalleryGrid,
      PDP_V2_FLAG
    } = this.props

    const {
      mainImageZoomed,
      currentSlide,
      orderedMedia,
      isMobile,
      trackingIndex,
      isTouch,
      componentIsFocused,
      isGalleryModalOpen,
      pausedIndex
    } = this.state

    const showZoomIcon =
      !isTouch &&
      !isEmpty(orderedMedia) &&
      orderedMedia[currentSlide] &&
      orderedMedia[currentSlide].type !== 'video' &&
      orderedMedia[currentSlide].type !== '360' &&
      orderedMedia[currentSlide].type !== '3D-AR'

    const sliderOptions = {
      showChevrons: !mainImageZoomed,
      showNavButtons: false,
      isInfiniteCycle: true,
      items: this.renderMainImages(),
      indexOfFirstDisplayedItem: 1,
      ref: this.sliderRef,
      itemsPerSlideXs: 1,
      itemsPerSlideSm: 1,
      itemsPerSlideMd: 1,
      itemsPerSlideLg: 1,
      currentSlide: isGalleryModalOpen ? pausedIndex : trackingIndex + 1,
      onSlideChanged: this.onSlideChangedCallback,
      disableSwipe: mainImageZoomed,
      innerAlignment: 'center'
    }

    const shopSimilarButton = showShopSimilarButton ? <ShopSimilarButton partNumber={partNumber} /> : null
    return (
      <Styled.Container
        data-test={dataTest}
        ref={this.mainRef}
        onFocus={() => this.setComponentFocus(true)}
        onBlur={() => this.setComponentFocus(false)}
      >
        {showMediaGalleryGrid && (
          <Styled.MediaGalleryGridWrapper>
            <MediaGalleryGrid media={this.renderMainImages(true)} shopSimilarButton={shopSimilarButton} />
          </Styled.MediaGalleryGridWrapper>
        )}
        <Styled.GalleryModalWrapper isModal={showMediaGalleryGrid}>
          <GalleryModal
            isModalOpen={isGalleryModalOpen}
            onModalClose={() => this.setState({ isGalleryModalOpen: false })}
            mediaArray={this.renderMainImages(false, true)}
            isCurrentAnImage={!isTouch && !isMobile && !!this.mainImageRef.current}
            isImageMagnified={mainImageZoomed}
            handleZoom={() => this.handleZoom()}
            thumbnailProps={{
              dataTest: `${dataTest}-thumbnails`,
              isPortrait: !isMobile && isPortrait,
              isMobile,
              style: isPortrait ? 'portrait' : 'landscape',
              structuredMedia: orderedMedia,
              onSlideChange: newIndex => this.updateTrackingIndex(newIndex),
              activeSlide: trackingIndex,
              isFocused: componentIsFocused,
              partNumber,
              model,
              gltf,
              addKeyDownListener: false,
              productName,
              updateTrackingIndex: this.updateTrackingIndex,
              trackingIndex
            }}
          />
        </Styled.GalleryModalWrapper>
        <Styled.Wrapper className='xs-row' isPortrait={isPortrait} showMediaGalleryGrid={showMediaGalleryGrid}>
          <Styled.SliderWrapper className={isPortrait ? 'xs-12--none md-9--none lg-10--none' : 'xs-12--none'}>
            {!this._isMounted && !isEmpty(orderedMedia) && this.renderDefaultImage()}
            <Slider {...sliderOptions} />
            {showZoomIcon && (
              <Styled.ZoomIconWrapper onClick={() => this.handleZoom()} data-test={`${dataTest}_zoomIcon`}>
                {mainImageZoomed ? <ZoomIcon dir='out' /> : <ZoomIcon dir='in' />}
              </Styled.ZoomIconWrapper>
            )}
            {shopSimilarButton}
            {!isEmpty(orderedMedia) && (
              <Styled.Nav clearBackground={PDP_V2_FLAG} data-test={`${dataTest}-thumbnail-nav`}>
                {`${trackingIndex + 1}/${orderedMedia.length}`}
              </Styled.Nav>
            )}
          </Styled.SliderWrapper>
          {!isEmpty(orderedMedia) && (
            <Styled.ThumbnailWrapper className={isPortrait && 'xs-12--none md-2--none'} isPortrait={isPortrait}>
              <ThumbnailComponent
                dataTest={`${dataTest}-thumbnails`}
                isPortrait={!isMobile && isPortrait}
                style={isPortrait ? 'portrait' : 'landscape'}
                structuredMedia={orderedMedia}
                onSlideChange={newIndex => this.updateTrackingIndex(newIndex)}
                activeSlide={trackingIndex}
                isFocused={componentIsFocused}
                partNumber={partNumber}
                model={model}
                gltf={gltf}
                productName={productName}
                addKeyDownListener
              />
            </Styled.ThumbnailWrapper>
          )}
        </Styled.Wrapper>
      </Styled.Container>
    )
  }
}

MediaGallery.propTypes = {
  showMediaGalleryGrid: PropTypes.bool,
  isPortrait: PropTypes.bool,
  isReviews: PropTypes.bool,
  showShopSimilarButton: PropTypes.bool,
  media: PropTypes.shape({
    images: PropTypes.array,
    videos: PropTypes.array,
    threesixty: PropTypes.array,
    meta: PropTypes.object
  }),
  'data-test': PropTypes.string,
  PDP_V2_FLAG: PropTypes.bool
}

MediaGallery.defaultProps = {
  showMediaGalleryGrid: false,
  isPortrait: false,
  isReviews: false,
  showShopSimilarButton: false,
  'data-test': 'component-media-gallery',
  PDP_V2_FLAG: false
}

export { MediaGallery, getImageDimensions }
export default withCookies(MediaGallery)
